import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Settings, ExternalLink, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../../shared/components/ui/Button';

const ConfigurationBanner: React.FC = () => {
  const { isConfigured, checkDatabaseConnection } = useAuth();
  const [isDatabaseIssue, setIsDatabaseIssue] = useState(false);
  const [isCheckingDb, setIsCheckingDb] = useState(false);

  // Check for database connection issues 
  useEffect(() => {
    if (isConfigured) {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('db_check') === 'true') {
        handleCheckDatabase();
      }
    }
  }, [isConfigured]);

  const handleCheckDatabase = async () => {
    setIsCheckingDb(true);
    try {
      const result = await checkDatabaseConnection();
      setIsDatabaseIssue(!result);
    } catch (error) {
      console.error('Database check error:', error);
      setIsDatabaseIssue(true);
    } finally {
      setIsCheckingDb(false);
    }
  };

  if (isConfigured) {
    // If there's a database issue, show a database-specific banner
    if (isDatabaseIssue) {
      return (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
          <div className="container mx-auto">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">
                  ⚠️ Database Connection Issue Detected
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  The application can see your database but can't access the profiles table. This might be a permission or schema issue.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCheckDatabase}
                  className="text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                  disabled={isCheckingDb}
                >
                  {isCheckingDb ? (
                    <>
                      <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Recheck
                    </>
                  )}
                </Button>
                <Link 
                  to="/login?repair=true"
                  className="inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 font-medium"
                >
                  <Settings className="h-3 w-3" />
                  Repair Profile
                </Link>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return null;
  }

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="bg-red-50 border-b border-red-200 px-4 py-3">
      <div className="container mx-auto">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">
              ⚠️ Application Configuration Required
            </p>
            <p className="text-xs text-red-700 mt-1">
              Missing Supabase environment variables. Please check your Netlify environment configuration.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              className="text-xs border-red-300 text-red-700 hover:bg-red-100"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Refresh
            </Button>
            <a
              href="https://docs.netlify.com/environment-variables/overview/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-red-700 hover:text-red-900 font-medium"
            >
              <Settings className="h-3 w-3" />
              Netlify Guide
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationBanner;